import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

/**
 * Derives the WebSocket base URL from the existing backend URL.
 * The chatbot runs on the same EC2 instance as the Flask backend,
 * so we reuse the same host (e.g. https://sistema-inventarios-backend.coopertsmith.com.mx).
 * Socket.IO will upgrade the connection to wss:// automatically.
 */
function getChatbotWsBaseUrl(): string {
  // environment.url is like "https://host/gmd/" — we only need the origin.
  try {
    return new URL(environment.url).origin;
  } catch {
    return environment.url;
  }
}

export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

export interface ChatbotError {
  code: string;
  message: string;
}

/**
 * Servicio que gestiona la conexión WebSocket (Socket.IO) del chatbot con el
 * backend, manejando la autenticación por JWT, la reconexión automática con
 * retroceso exponencial y la emisión reactiva de tokens, errores y estado de
 * conexión hacia los componentes suscritos.
 */
@Injectable({
  providedIn: 'root'
})
export class ChatbotWebsocketService implements OnDestroy {

  private socket: Socket | null = null;

  // --- Reconnection state ---
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10;
  private readonly baseReconnectDelayMs = 1000;
  private readonly maxReconnectDelayMs = 30000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalDisconnect = false;
  private currentJwtToken: string | null = null;

  // --- Reactive streams ---
  private connectionStatusSubject = new BehaviorSubject<ConnectionStatus>('disconnected');
  private tokenStreamSubject = new Subject<string>();
  private messageCompleteSubject = new Subject<void>();
  private errorSubject = new Subject<ChatbotError>();

  /** Emits the current WebSocket connection state. */
  connectionStatus$: Observable<ConnectionStatus> = this.connectionStatusSubject.asObservable();

  /** Emits each LLM token as it arrives from the backend. */
  tokenStream$: Observable<string> = this.tokenStreamSubject.asObservable();

  /** Emits once when the LLM has finished generating a response. */
  messageComplete$: Observable<void> = this.messageCompleteSubject.asObservable();

  /** Emits structured error events received from the backend. */
  error$: Observable<ChatbotError> = this.errorSubject.asObservable();

  constructor(private router: Router) {}

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Abre una conexión Socket.IO con el servidor WebSocket del chatbot en EC2.
   * El token JWT se envía como parámetro de consulta para que Flask-SocketIO
   * lo valide en el evento `connect`.
   * @param jwtToken - Token JWT en crudo (sin el prefijo "GMD-").
   */
  connect(jwtToken: string): void {
    if (this.socket?.connected) {
      return;
    }

    this.intentionalDisconnect = false;
    this.currentJwtToken = jwtToken;
    this._createSocket(jwtToken);
  }

  /**
   * Cierra de forma limpia la conexión WebSocket y cancela cualquier intento
   * de reconexión pendiente.
   */
  disconnect(): void {
    this.intentionalDisconnect = true;
    this._cancelReconnect();
    this.reconnectAttempts = 0;
    this.currentJwtToken = null;

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.connectionStatusSubject.next('disconnected');
  }

  /**
   * Emite un evento `sendMessage` hacia el backend Flask-SocketIO.
   * @param message - Consulta del usuario en lenguaje natural.
   * @param sessionId - Identificador de la sesión de conversación activa.
   */
  sendMessage(message: string, sessionId: string): void {
    if (!this.socket?.connected) {
      this.errorSubject.next({
        code: 'NOT_CONNECTED',
        message: 'No hay conexión WebSocket activa. Por favor, reconecta e intenta de nuevo.'
      });
      return;
    }

    this.socket.emit('sendMessage', { message, session_id: sessionId });
  }

  /**
   * Devuelve el estado de conexión actual de forma síncrona.
   * @returns Estado de conexión actual.
   */
  get connectionStatus(): ConnectionStatus {
    return this.connectionStatusSubject.value;
  }

  /**
   * Indica si el socket está conectado actualmente.
   * @returns `true` cuando el socket está conectado.
   */
  get isConnected(): boolean {
    return this.connectionStatusSubject.value === 'connected';
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnDestroy(): void {
    this.disconnect();
    this.connectionStatusSubject.complete();
    this.tokenStreamSubject.complete();
    this.messageCompleteSubject.complete();
    this.errorSubject.complete();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private _createSocket(jwtToken: string): void {
    // Disable Socket.IO's built-in reconnection — we handle it manually
    // with exponential backoff so we can refresh the JWT if needed.
    this.socket = io(getChatbotWsBaseUrl() + '/chatbot', {
      path: '/chatbot',
      query: { token: jwtToken },
      transports: ['websocket'],
      reconnection: false,
      timeout: 10000,
    });

    this._registerSocketEvents();
  }

  private _registerSocketEvents(): void {
    if (!this.socket) return;

    // --- Connection established ---
    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
      this.connectionStatusSubject.next('connected');
    });

    // --- Connection lost ---
    this.socket.on('disconnect', (reason: string) => {
      this.connectionStatusSubject.next('disconnected');

      if (this.intentionalDisconnect) {
        return;
      }

      // Socket.IO server-initiated disconnects (e.g. auth failure) should
      // not trigger automatic reconnection.
      if (reason === 'io server disconnect') {
        return;
      }

      this._scheduleReconnect();
    });

    // --- Connection error ---
    this.socket.on('connect_error', (err: Error) => {
      const message = err.message || 'Error de conexión WebSocket';

      // Detect authentication errors (401 / token expired / invalid token).
      if (this._isAuthError(message)) {
        this.intentionalDisconnect = true;
        this._cancelReconnect();
        this.connectionStatusSubject.next('disconnected');
        this.errorSubject.next({ code: 'AUTH_ERROR', message });
        this.router.navigate(['/login']);
        return;
      }

      this.connectionStatusSubject.next('disconnected');
      this._scheduleReconnect();
    });

    // --- Incoming LLM token ---
    this.socket.on('token', (data: { token: string }) => {
      if (data?.token !== undefined) {
        this.tokenStreamSubject.next(data.token);
      }
    });

    // --- LLM response complete ---
    this.socket.on('messageComplete', () => {
      this.messageCompleteSubject.next();
    });

    // --- Structured error from backend ---
    this.socket.on('error', (data: ChatbotError) => {
      if (data?.code && data?.message) {
        // Auth errors mid-session (e.g. token expired) redirect to login.
        if (data.code === 'AUTH_ERROR' || data.code === '401') {
          this.intentionalDisconnect = true;
          this._cancelReconnect();
          this.disconnect();
          this.router.navigate(['/login']);
        }
        this.errorSubject.next(data);
      }
    });
  }

  /**
   * Schedules a reconnection attempt using exponential backoff.
   * Delay = min(baseDelay * 2^attempt, maxDelay)
   */
  private _scheduleReconnect(): void {
    if (this.intentionalDisconnect) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.errorSubject.next({
        code: 'MAX_RECONNECT_EXCEEDED',
        message: 'Se superó el número máximo de intentos de reconexión.'
      });
      return;
    }

    this.connectionStatusSubject.next('reconnecting');

    const delay = Math.min(
      this.baseReconnectDelayMs * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelayMs
    );

    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      if (this.intentionalDisconnect || !this.currentJwtToken) return;

      // Destroy the old socket before creating a new one.
      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }

      this._createSocket(this.currentJwtToken);
    }, delay);
  }

  private _cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Heuristic to detect authentication-related connection errors.
   * Flask-SocketIO surfaces these as connection error messages.
   */
  private _isAuthError(message: string): boolean {
    const lower = message.toLowerCase();
    return (
      lower.includes('401') ||
      lower.includes('unauthorized') ||
      lower.includes('invalid token') ||
      lower.includes('token expired') ||
      lower.includes('authentication')
    );
  }
}
